import { del, get, patch, post } from '@elementary/common-ui/utils/http';
import { FetchTeamUsersApi, Page } from 'shared/types';
import {
  Ticket,
  FetchIncidentOverviewApi,
  FetchIncidentsApi,
  Incident,
} from './types';

export const fetchIncidents = (
  envId: string,
  page?: number,
  size?: number,
  filters?: string,
  daysBack?: number,
  search?: string,
  assetIds?: string[],
): Promise<Page<Incident>> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/get-incidents`,
    {
      days_back: daysBack,
      search,
      asset_ids: assetIds,
    },
    {
      params: {
        page,
        size,
        filters,
      },
    },
  );

export const fetchIncidentFilters = (
  envId: string,
  search?: string,
): Promise<FetchIncidentsApi> =>
  get(`${window.VITE_API_BASE_URL}/${envId}/incidents/filter-options`, {
    search,
  });

export const fetchIncidentOverview = (
  envId: string,
  daysBack?: number,
  filters?: string,
  search?: string,
  assetIds?: string[],
): Promise<FetchIncidentOverviewApi> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/get-incidents/overview`,
    {
      days_back: daysBack,
      search,
      asset_ids: assetIds,
    },
    {
      params: {
        filters,
      },
    },
  );

export const fetchAssignees = (envId: string): Promise<FetchTeamUsersApi> =>
  get(`${window.VITE_API_BASE_URL}/${envId}/incidents/assignees`);

export const updateIncidents = (
  envId: string,
  all?: boolean,
  exclude?: string[],
  include?: string[],
  daysBack?: number,
  filters?: string,
  search?: string,
  assignee?: string,
  status?: string,
  severity?: string,
) =>
  patch(
    `${window.VITE_API_BASE_URL}/${envId}/incidents`,
    {
      all,
      exclude,
      include,
      update: {
        assignee,
        status,
        severity,
      },
    },
    {
      params: {
        days_back: daysBack,
        filters,
        search,
      },
    },
  );

export const updateIncident = (
  envId: string,
  incidentId: string,
  update: {
    assignee?: string;
    status?: string;
    severity?: string;
  },
) =>
  patch(`${window.VITE_API_BASE_URL}/${envId}/incidents`, {
    include: [incidentId],
    update,
  });

export const createIncidentTicket = (
  envId: string,
  incidentId: string,
  incidentUrl: string,
  integrationId: string,
  customFields?: Record<string, any>,
): Promise<Ticket> =>
  post(`${window.VITE_API_BASE_URL}/${envId}/incidents/${incidentId}/ticket`, {
    incident_url: incidentUrl,
    ticketing_integration_id: integrationId,
    custom_fields: customFields,
  });

export const fetchDefaultIncidentFields = (
  envId: string,
  incidentId: string,
  integrationId: string,
  incidentUrl: string,
): Promise<{
  default_fields: {
    fields: Record<string, any>;
  };
}> =>
  get(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/${incidentId}/ticket/default-fields`,
    {
      ticketing_integration_id: integrationId,
      incident_url: incidentUrl,
    },
  );

export type SummaryResponse = {
  id: string;
  incident_id: string;
  summary_type: string;
  content: string;
  created_at: string;
  updated_at: string;
};

export const createIncidentSummary = (
  envId: string,
  incidentId: string,
  summaryType: string,
  content: string,
): Promise<SummaryResponse> =>
  post(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/${incidentId}/summaries/${summaryType}`,
    { content },
  );

export const updateIncidentSummary = (
  envId: string,
  incidentId: string,
  summaryType: string,
  content: string,
): Promise<SummaryResponse> =>
  patch(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/${incidentId}/summaries/${summaryType}`,
    { content },
  );

export const deleteIncidentSummary = (
  envId: string,
  incidentId: string,
  summaryType: string,
): Promise<void> =>
  del(
    `${window.VITE_API_BASE_URL}/${envId}/incidents/${incidentId}/summaries/${summaryType}`,
  );
